import { AlertType } from "../ui-elements/Alert/Alert";

/** A single company in the watchlist (from GET /Pep/GetCompanyWatchList) */
export interface CompanyWatchItem {
  id: {
    ownerId: number;
    companyId: string;
  };
  name: string;
  pepFrequency: string;
  sanctionsFrequency: string;
  adilarList: string[];
  connectionCount: number | null;
  pepOrSanctionsCount: number | null;
  pepCount: number | null;
  sanctionsCount: number | null;
}

/** Request body for adding companies to watchlist */
export interface AddCompanyWatchRequest {
  companyId: string;
  pepFrequency?: string;
  sanctionsFrequency?: string;
  adilarIVoktun?: AdilarRole[];
}

/** Valid roles for adilarIVoktun */
export type AdilarRole = "stjorn" | "prokuruhafi" | "framkvaemdastjorn" | "raunverulegir_eigendur";

/** Icelandic display labels for each role */
export const ADILAR_ROLE_LABELS: Record<AdilarRole, string> = {
  raunverulegir_eigendur: "Raunverulegir eigendur",
  prokuruhafi: "Prókúruhafi",
  framkvaemdastjorn: "Framkvæmdastjórn",
  stjorn: "Stjórn",
};

export const ALL_ADILAR_ROLES: AdilarRole[] = [
  "raunverulegir_eigendur",
  "prokuruhafi",
  "framkvaemdastjorn",
  "stjorn",
];

/** Filter types for the company watch table */
export type CompanyPepFilter = "showAll" | "yes" | "no" | "active" | "inactive";
export type CompanySanctionsFilter = "showAll" | "yes" | "no" | "active" | "inactive";
export type CompanyConnectionsFilter = "showAll" | "active" | "inactive" | "flagged" | "notFlagged";

/** Consolidated filter state for the company table */
export interface CompanyFilterState {
  searchTerm: string;
  pepStatus: CompanyPepFilter;
  sanctionsStatus: CompanySanctionsFilter;
  connectionsFilter: CompanyConnectionsFilter;
  sortBy: "name" | null;
  sortOrder: "asc" | "desc" | null;
}

/** Props shared by company watch modals */
export interface CompanyWatchModalProps {
  show: boolean;
  close(): void;
  setToastMessage(message: string): void;
  setToastType(type: AlertType): void;
  setToastShow(show: boolean): void;
  onSuccess(): void;
}

/** Base fields shared by all company event types */
export interface CompanyEventBase {
  id: string;
  ownerId: number;
  companyId: string;
  eventTimestamp: string;
  eventType: string;
}

export interface ValueChangeEvent {
  key: string;
  oldValue: string[];
  newValue: string[];
}

export interface PersonChangeEvent {
  name: string;
  kennitala: string;
  role: string;
  addedOrRemoved: string;
}

export interface IndividualValueChangeEvent {
  name: string;
  kennitala: string;
  key: string;
  oldValue: string[];
  newValue: string[];
}

export interface CompanyChangeEvent extends CompanyEventBase {
  eventType: "company_change";
  valueChanges: ValueChangeEvent[];
  peopleChanges: PersonChangeEvent[];
}

export interface CompanyWatchChangeEvent extends CompanyEventBase {
  eventType: "company_watch_change" | "watch_change";
  valueChanges: ValueChangeEvent[];
}

export interface CompanyStatusUpdateEvent extends CompanyEventBase {
  eventType: "status_update" | "individual_status_change";
  personId: string;
  name: string;
  key: string;
  oldStatus: boolean | null;
  newStatus: boolean | null;
}

/** Special events marking watch start/stop (empty JSON body) */
export interface CompanyWatchLifecycleEvent extends CompanyEventBase {
  eventType: "company_watch_start" | "company_watch_stop";
}

export interface IndividualWatchChangeEvent extends CompanyEventBase {
  eventType: "individual_watch_change" | "individual_change";
  valueChanges: IndividualValueChangeEvent[];
}

export interface CompanyEventsData {
  companyChangeEvents: CompanyChangeEvent[];
  companyWatchChangeEvents: CompanyWatchChangeEvent[];
  statusUpdateEvents: CompanyStatusUpdateEvent[];
  individualWatchChangeEvents: IndividualWatchChangeEvent[];
  lifecycleEvents?: CompanyWatchLifecycleEvent[];
}
